import ColorScheme, { colorSchemeToCss } from 'mini-color-utils/lib/ColorScheme';
import { addOpacityToColor, computeResultingOverlapColor } from 'mini-color-utils/lib/colorUtils';
import { classNamesFunction } from '@uifabric/utilities';
import { EventCardStyleProps, EventCardStyles } from '../schema/EventCard.types';
import { FreeBusyConstants, FreeBusyTypeString } from 'mini-freebusy/lib/schema/FreeBusyTypeString';
import { getOptimalTextColor } from 'mini-color-utils/lib/OptimalTextColorCache';
import { IStyle } from '@uifabric/styling';
import {
    OPACITY_FREE,
    OPACITY_TENTATIVE,
    OPACITY_BUSY,
    MINIMUM_TEXT_CONTRAST_REST,
} from '../helpers/constants';

const NULL_KEY = 'NULL';
const WHITE_COLOR_HEX_CODE = '#FFFFFF';
const NEUTRAL_SECONDARY_ALT_COLOR_HEX_CODE = '#767676';
const NEUTRAL_PRIMARY_COLOR_HEX_CODE = '#333333';
const OPACITY_CANCELLED = 0.08;

/**
 * Map to store colorSchemeToCss computations
 */
const colorSchemeToCssMap = new Map<string, React.CSSProperties>();

/**
 * Map to store itemStyles computations
 */
const itemStylesMap = new Map<string, EventCardStyles>();

const getColorSchemeToCss = colorScheme => {
    const key = colorScheme ? colorScheme.schemePrimary : NULL_KEY;
    let result = colorSchemeToCssMap.get(key);
    if (!result) {
        result = colorSchemeToCss(colorScheme);
        colorSchemeToCssMap.set(key, result);
    }
    return result;
};

/**
 * Gets styles for a given item based on item properties
 * @param props styling properties of the item
 */
export const getEventCardStyles = (props: EventCardStyleProps): EventCardStyles => {
    const { freeBusyType, colorScheme, isCancelled, isAllDay = false } = props;

    const colorSchemeKey = colorScheme ? colorScheme.schemePrimary : NULL_KEY;

    // The key can be like Busy_#0078D4_false or Tentative_#0f4f4f4_true.
    const key = freeBusyType + '_' + colorSchemeKey + '_' + isCancelled + '_' + isAllDay;

    let styles = itemStylesMap.get(key);
    if (!styles) {
        styles = {
            content: getEventCardContentStyles(freeBusyType, colorScheme, isCancelled, isAllDay),
        };
        itemStylesMap.set(key, styles);
    }

    return styles;
};

/**
 * Gets the styles for just the content of the item, not including the free busy bar
 * @param freeBusyType the item free busy type
 * @param responseType the item response type
 * @param colorScheme the item color scheme
 * @param isCancelled the cancel status of the item
 */
export const getEventCardContentStyles = (
    freeBusyType: FreeBusyTypeString,
    colorScheme: ColorScheme,
    isCancelled: boolean,
    isAllDay?: boolean
): IStyle => {
    const colorCssNormal = getColorSchemeToCss(colorScheme);
    const backgroundColor = colorCssNormal.backgroundColor;
    const colorLighter = colorScheme.schemeLight;
    let textColor = colorCssNormal.color;
    const minimumTextContrastRest = MINIMUM_TEXT_CONTRAST_REST;

    const opacity = calculateOpacity(freeBusyType);

    /**
     * Calculate the background colors for the different interactive states, including their opacity
     */
    const transparentBackground = addOpacityToColor(backgroundColor, opacity);

    /**
     * Since the background colors have opacity, compute the RESULTING color from overlaying that opacity with the surface ms-color-white background
     * so that we know what color we're working with when computing an accessible text color
     */
    const totalBackgroundColor = computeResultingOverlapColor(
        transparentBackground,
        WHITE_COLOR_HEX_CODE
    );

    /**
     * Using the calculated true background colors, calculate accessible text colors
     */
    textColor = getOptimalTextColor(
        totalBackgroundColor,
        colorScheme,
        WHITE_COLOR_HEX_CODE,
        minimumTextContrastRest
    );

    const borderColor = freeBusyType === FreeBusyConstants.Free ? colorLighter : backgroundColor;
    const isFreeAllDay = freeBusyType === FreeBusyConstants.Free && isAllDay;

    /**
     * if item not responded to, use tentative styling with dashed border
     */
    const border = `1px solid ${borderColor}`;

    return {
        backgroundColor: isCancelled
            ? addOpacityToColor(NEUTRAL_SECONDARY_ALT_COLOR_HEX_CODE, OPACITY_CANCELLED)
            : transparentBackground,
        border: border,
        color: isCancelled ? NEUTRAL_PRIMARY_COLOR_HEX_CODE : textColor,
        fill: textColor,
        borderLeftColor: !isFreeAllDay ? backgroundColor : null,
    };
};

/**
 * Returns opacity based on free busy status of an item
 * @param freeBusyType the item free busy type
 */
function calculateOpacity(freeBusyType: FreeBusyTypeString): number {
    let opacity = OPACITY_BUSY;

    switch (freeBusyType) {
        case FreeBusyConstants.Tentative:
            opacity = OPACITY_TENTATIVE;
            break;
        case FreeBusyConstants.Free:
        case FreeBusyConstants.WorkingElsewhere:
            opacity = OPACITY_FREE;
    }

    return opacity;
}

export const getEventCardClassNames = classNamesFunction<EventCardStyleProps, EventCardStyles>();
